import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { SiteLayout } from "@/components/site/Layout";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { CTABanner } from "@/components/site/CTABanner";
import { PILLARS, type Pillar } from "@/lib/pillars";
import { cn } from "@/lib/utils";

export type PillarSection = {
  eyebrow?: string;
  heading: string;
  body?: string;
  bullets?: string[];
  tone?: "dark" | "darker" | "cream";
};

export type PillarFAQ = { q: string; a: string };

export function PillarPage({
  pillar,
  intro,
  sections,
  benefits,
  faqs,
}: {
  pillar: Pillar;
  intro: string;
  sections: PillarSection[];
  benefits?: { heading: string; items: string[] };
  faqs?: PillarFAQ[];
}) {
  const related = PILLARS.filter((p) => p.slug !== pillar.slug);

  return (
    <SiteLayout>
      {/* Hero (breadcrumbs sit inside the hero image) */}
      <section className="relative pt-28 md:pt-32 pb-14 border-b border-border overflow-hidden isolate">
        <div className="absolute inset-0 -z-10">
          <img
            src={pillar.heroImage}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover scale-105"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/85 via-background/75 to-background/95" />
          <div className="absolute inset-0 bg-background/40" />
        </div>

        <nav
          aria-label="Breadcrumb"
          className="container-edge pb-8 text-xs uppercase tracking-[0.22em] text-foreground/55"
        >
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link to="/" className="hover:text-foreground">Home</Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>
              <Link to="/why-join-kcn" className="hover:text-foreground">Why Join</Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-foreground/80">{pillar.title} {pillar.italic}</li>
          </ol>
        </nav>

        <div className="container-edge grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow mb-6">{pillar.eyebrow} - Inside KCN</p>
            <h1 className="headline-xl">
              {pillar.title}{" "}
              <span className="serif-italic text-primary">{pillar.italic}</span>
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="text-foreground/90 text-lg leading-relaxed">
              {pillar.lead}
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 md:py-14 border-b border-border">
        <div className="container-edge">
          <p className="text-foreground text-xl md:text-2xl leading-[1.6] max-w-3xl font-light">
            {intro}
          </p>
        </div>
      </section>



      {/* Body sections */}
      {sections.map((s, i) => {
        const tone = s.tone ?? (i % 2 === 0 ? "cream" : "dark");
        const isCream = tone === "cream";

        return (
        <Section key={s.heading} tone={tone}>
          {s.eyebrow ? (
            <SectionEyebrow className={isCream ? "text-cream-foreground/55" : undefined}>
              {s.eyebrow}
            </SectionEyebrow>
          ) : null}
          <div className="grid lg:grid-cols-12 gap-10">
            <h2 className="headline-lg lg:col-span-5 max-w-md">{s.heading}</h2>
            <div className="lg:col-span-7 lg:col-start-6 space-y-6">
              {s.body ? (
                <p
                  className={cn(
                    "text-[1.05rem] md:text-[1.125rem] leading-[1.75]",
                    isCream ? "text-cream-foreground/90" : "text-foreground/90",
                  )}
                >
                  {s.body}
                </p>
              ) : null}
              {s.bullets ? (
                <ul
                  className={cn(
                    "space-y-3 border-t pt-6",
                    isCream ? "border-cream-foreground/20" : "border-border/60",
                  )}
                >
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className={cn(
                        "flex items-start gap-3 text-base md:text-[1.05rem]",
                        isCream ? "text-cream-foreground/90" : "text-foreground/90",
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className="mt-2.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0"
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </Section>
        );
      })}

      {/* Benefits */}
      {benefits ? (
        <Section tone="darker">
          <SectionEyebrow>What you actually get</SectionEyebrow>
          <h2 className="headline-lg mb-10 max-w-3xl">{benefits.heading}</h2>
          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 border-t border-border/60">
            {benefits.items.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 border-b border-border/60 py-4 text-foreground/90 text-base md:text-[1.05rem]"
              >
                <span
                  aria-hidden="true"
                  className="mt-2.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {/* FAQs */}
      {faqs && faqs.length > 0 ? (
        <Section tone="dark">
          <SectionEyebrow>Questions agents ask</SectionEyebrow>
          <h2 className="headline-lg mb-10 max-w-3xl">
            About {pillar.title.toLowerCase()} {pillar.italic.toLowerCase()}.
          </h2>
          <div className="divide-y divide-border border-y border-border">
            {faqs.map((f) => (
              <details key={f.q} className="py-6 group">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <h3 className="font-display text-xl md:text-2xl pr-4">{f.q}</h3>
                  <span className="text-primary text-2xl leading-none mt-1 group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-foreground/90 leading-relaxed max-w-3xl">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </Section>
      ) : null}

      {/* Related pillars */}
      <Section tone="cream">
        <SectionEyebrow>The other pillars</SectionEyebrow>
        <h2 className="headline-lg mb-10 max-w-3xl">
          Every pillar reinforces the next.
        </h2>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-cream-foreground/15">
          {related.map((p) => (
            <li key={p.slug} className="bg-cream">
              <Link
                to={p.path}
                className="block p-6 group h-full hover:bg-cream-foreground/5 transition-colors"
              >
                <p className="text-[0.7rem] uppercase tracking-[0.22em] text-cream-foreground/60 mb-3">
                  {p.eyebrow}
                </p>
                <h3 className="font-display text-2xl text-cream-foreground mb-2">
                  {p.title}{" "}
                  <span className="italic text-primary">{p.italic}</span>
                </h3>
                <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-primary mt-2">
                  Explore <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Section>

      <CTABanner />
    </SiteLayout>
  );
}

export function buildPillarHead(
  pillar: Pillar,
  faqs?: PillarFAQ[],
) {
  const scripts: Array<{ type: string; children: string }> = [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://joinkcn.com/" },
          { "@type": "ListItem", position: 2, name: "Why Join", item: "https://joinkcn.com/why-join-kcn" },
          {
            "@type": "ListItem",
            position: 3,
            name: `${pillar.title} ${pillar.italic}`,
            item: `https://joinkcn.com${pillar.path}`,
          },
        ],
      }),
    },
  ];

  if (faqs && faqs.length > 0) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    });
  }

  return {
    meta: [
      { title: pillar.metaTitle },
      { name: "description", content: pillar.metaDescription },
      { property: "og:title", content: pillar.metaTitle },
      { property: "og:description", content: pillar.metaDescription },
      { property: "og:url", content: `https://joinkcn.com${pillar.path}` },
      { property: "og:type", content: "article" },
    ],
    links: [
      { rel: "canonical", href: `https://joinkcn.com${pillar.path}` },
    ],
    scripts,
  };
}
